import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";

import Reveal from "@/components/public/Reveal";
import { publicApi } from "@/lib/api";
import { FALLBACK_TEAM, FALLBACK_TESTIMONIALS, getTeamMedia } from "@/lib/company";

const FALLBACK_HERO = {
  headline: "We design the blueprint for your life’s story.",
  subheadline:
    "Residential design and BIM production shaped through collective work, technical precision and a deeply personal design process.",
  backgrounds: ["/assets/minga/projects/waters-residence/02-dining.jpg"],
};

const TESTIMONIAL_THEMES = [
  { bg: "#E9C947", fg: "#000000", quote: "rgba(0,0,0,0.42)", label: "rgba(0,0,0,0.58)" },
  { bg: "#77CA65", fg: "#000000", quote: "rgba(0,0,0,0.38)", label: "rgba(0,0,0,0.58)" },
  { bg: "#C64B3E", fg: "#FAFAFA", quote: "rgba(250,250,250,0.58)", label: "rgba(250,250,250,0.72)" },
  { bg: "#4472B4", fg: "#FAFAFA", quote: "rgba(250,250,250,0.58)", label: "rgba(250,250,250,0.72)" },
  { bg: "#6D88F2", fg: "#FAFAFA", quote: "rgba(250,250,250,0.6)", label: "rgba(250,250,250,0.74)" },
];

const TESTIMONIAL_AUTOPLAY_MS = 75000;

function getTestimonialTheme(index) {
  return TESTIMONIAL_THEMES[index % TESTIMONIAL_THEMES.length];
}

export default function HomePage() {
  const [page, setPage] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [team, setTeam] = useState([]);
  const [servicePathways, setServicePathways] = useState([]);
  const [activeHero, setActiveHero] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialAutoplay, setTestimonialAutoplay] = useState(true);

  useEffect(() => {
    publicApi.track({ event_type: "page_view", page: "/" }).catch(() => {});
    publicApi.getPage("home").then(setPage).catch(() => {});
    publicApi.getTestimonials().then(setTestimonials).catch(() => {});
    publicApi.getTeam().then(setTeam).catch(() => {});
    publicApi.getServices("residential").then(setServicePathways).catch(() => {});
  }, []);

  const hero = page?.content?.hero || FALLBACK_HERO;
  const collective = page?.content?.collective;
  const finalCta = page?.content?.final_cta;
  const serviceIntro = page?.content?.service_intro;
  const backgrounds = hero.backgrounds?.length ? hero.backgrounds : FALLBACK_HERO.backgrounds;
  const displayedTestimonials = testimonials.length ? testimonials : FALLBACK_TESTIMONIALS;
  const displayedTeam = team.length ? team : FALLBACK_TEAM;

  useEffect(() => {
    if (backgrounds.length < 2) return;
    const t = setInterval(() => {
      setActiveHero((i) => (i + 1) % backgrounds.length);
    }, 6500);
    return () => clearInterval(t);
  }, [backgrounds]);

  useEffect(() => {
    if (!testimonialAutoplay) return;
    if (displayedTestimonials.length < 2) return;

    const timer = setInterval(() => {
      setActiveTestimonial((index) => (index + 1) % displayedTestimonials.length);
    }, TESTIMONIAL_AUTOPLAY_MS);

    return () => clearInterval(timer);
  }, [displayedTestimonials.length, testimonialAutoplay]);

  const handleSelectTestimonial = (index) => {
    setActiveTestimonial(index);
    setTestimonialAutoplay(false);
  };

  return (
    <main className="home-subtle-motion">
      {/* HERO */}
      <section
        className="relative h-[100svh] min-h-[640px] w-full overflow-hidden home-hero-stage"
        data-testid="home-hero"
      >
        {backgrounds.map((src, i) => (
          <div
            key={src + i}
            className={`absolute inset-0 transition-opacity duration-[2200ms] ease-out home-hero-bg ${
              i === activeHero ? "opacity-100" : "opacity-0"
            }`}
            style={{
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transform: i === activeHero ? "scale(1.02)" : "scale(1)",
              transition: "opacity 2.2s ease, transform 12s ease",
            }}
            aria-hidden="true"
          />
        ))}

        <div className="absolute inset-0 bg-gradient-to-b from-ink-black/70 via-ink-black/40 to-ink-black" />
        <div className="absolute inset-0 blueprint opacity-[0.35]" />

        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1440px] mx-auto px-5 md:px-10 pb-16 md:pb-24 home-hero-content">
          <p className="eyebrow animate-fade-in home-hero-line home-hero-line-1">
            {hero.eyebrow || "[ Residential Design · BIM Production ]"}
          </p>
          <h1 className="hero-headline text-brand-white mt-6 max-w-5xl animate-fade-up home-hero-line home-hero-line-2">
            {hero.headline}
          </h1>
          <p className="body-text mt-6 max-w-2xl text-brand-white/85 text-base md:text-lg animate-fade-up home-hero-line home-hero-line-3">
            {hero.subheadline}
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-9 animate-fade-up home-hero-line home-hero-line-4">
            <Link
              to={hero.secondary_cta?.link || "/residential-design"}
              className="btn-outline"
              data-testid="hero-secondary-cta"
            >
              {hero.secondary_cta?.label || "Explore Residential Design"}
            </Link>
            <Link
              to={hero.tertiary_cta?.link || "/bim-production"}
              className="btn-ghost link-underline"
              data-testid="hero-tertiary-cta"
            >
              {hero.tertiary_cta?.label || "BIM Support for Architects"}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {backgrounds.length > 1 && (
            <div className="absolute right-5 md:right-10 bottom-6 md:bottom-10 flex flex-col gap-2">
              {backgrounds.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveHero(i)}
                  aria-label={`Show hero slide ${i + 1}`}
                  className={`h-6 w-[2px] transition-colors ${
                    i === activeHero ? "bg-brand-yellow" : "bg-white/25"
                  }`}
                />
              ))}
            </div>
          )}

          <div className="hidden md:block absolute left-5 md:left-10 bottom-6 text-[10px] uppercase tracking-[0.32em] text-brand-white/60">
            Seattle, WA — Quito, Ecuador
          </div>
        </div>
      </section>

      {/* COLLECTIVE WORK */}
      {collective && (
        <section className="section-pad max-w-[1440px] mx-auto px-5 md:px-10 home-motion-section">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <Reveal className="lg:col-span-6 home-reveal-left">
              <p className="eyebrow">{collective.eyebrow || "[ Collective Work ]"}</p>
              <h2 className="section-headline mt-5 max-w-2xl">{collective.headline}</h2>
            </Reveal>
            <Reveal className="lg:col-span-6 lg:pt-4 home-reveal-right" delay={110}>
              <p className="body-text">{collective.paragraph}</p>
              <div className="relative mt-10 border-l-2 border-brand-yellow pl-6 home-quote-line">
                <p className="text-xl md:text-2xl font-medium leading-snug text-brand-white/95 tracking-tight">
                  “{collective.quote}”
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* TWO PATHWAYS */}
      <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10 home-motion-section">
        <Reveal className="home-section-heading">
          <p className="eyebrow">{serviceIntro?.eyebrow || "[ Two Pathways ]"}</p>
          <h2 className="section-headline mt-4 max-w-3xl">
            {serviceIntro?.headline || "Two disciplines. One collaborative studio."}
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12">
          {(servicePathways.length
            ? servicePathways
            : [
                {
                  title: "Residential Design",
                  subtitle: "Design a new home, remodel and more.",
                  image: backgrounds[0],
                  cta_label: "View Residential Work",
                  cta_link: "/residential-design",
                },
                {
                  title: "BIM Production",
                  subtitle: "High-quality Revit© modeling for your firm.",
                  image: backgrounds[1] || backgrounds[0],
                  cta_label: "Explore BIM Production",
                  cta_link: "/bim-production",
                },
              ]
          )
            .slice(0, 2)
            .map((service, index) => (
              <PathwayCard
                key={service.id || service.title}
                eyebrow={`[ ${service.title} ]`}
                title={service.subtitle || service.description || service.title}
                image={service.image || backgrounds[index] || backgrounds[0]}
                cta={service.cta_label || "Explore"}
                to={service.cta_link || (index === 0 ? "/residential-design" : "/bim-production")}
                accent={index === 1 ? "green" : "yellow"}
                delay={index * 120}
              />
            ))}
        </div>
      </section>

      {/* OUR TEAM */}
      <section id="team-preview" className="section-pad max-w-[1440px] mx-auto px-5 md:px-10 home-motion-section">
        <Reveal className="home-section-heading">
          <p className="eyebrow">{page?.content?.team_intro?.eyebrow || "[ Our Team ]"}</p>
          <h2 className="section-headline mt-4 max-w-3xl">
            {page?.content?.team_intro?.headline || "The people behind the process."}
          </h2>
          <p className="body-text mt-5 max-w-2xl">
            {page?.content?.team_intro?.paragraph ||
              "Meet the architects shaping Minga’s residential design and BIM production work."}
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 md:gap-10 mt-12">
          {displayedTeam.slice(0, 2).map((member, index) => (
            <HomeTeamCard key={member.id || member.name} member={member} index={index} />
          ))}
        </div>

        <Reveal className="mt-10 flex justify-center md:justify-end home-reveal-soft" delay={120}>
          <Link to={page?.content?.team_intro?.cta?.link || "/about#team-gallery"} className="btn-primary">
            {page?.content?.team_intro?.cta?.label || "View Full Team Gallery"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* CLIENT STORIES */}
      <section className="section-pad bg-ink-charcoal/70 border-y border-white/6 overflow-hidden home-motion-section home-testimonials-section">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 home-section-heading">
            <p className="eyebrow">{page?.content?.testimonials_intro?.eyebrow || "[ Client Stories ]"}</p>
            <h2 className="section-headline mt-4 max-w-2xl">
              {page?.content?.testimonials_intro?.headline || "Words from the people who lived the process."}
            </h2>
            <p className="body-text mt-5 max-w-2xl">
              {page?.content?.testimonials_intro?.paragraph ||
                "Real client testimonials from Minga residential projects."}
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-stretch">
            <Reveal className="lg:col-span-7 relative home-reveal-left" delay={80}>
              {displayedTestimonials.map((t, i) => (
                <TestimonialCard
                  key={t.id || i}
                  t={t}
                  visible={i === activeTestimonial}
                  index={i}
                />
              ))}
            </Reveal>

            <Reveal className="lg:col-span-5 grid gap-3 home-reveal-right" delay={160}>
              {displayedTestimonials.map((t, i) => {
                const theme = getTestimonialTheme(i);
                const active = i === activeTestimonial;

                return (
                  <button
                    key={t.id || i}
                    type="button"
                    onClick={() => handleSelectTestimonial(i)}
                    className={`testimonial-orbit-card group text-left p-5 md:p-6 border transition-all duration-300 ${
                      active ? "" : "border-white/8 bg-white/[0.025]"
                    }`}
                    style={
                      active
                        ? {
                            borderColor: theme.bg,
                            backgroundColor: `${theme.bg}20`,
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-brand-white font-semibold tracking-tight">{t.client_name}</p>
                        {t.location ? (
                          <p className="text-xs uppercase tracking-[0.18em] text-brand-gray mt-1">
                            {t.location}
                          </p>
                        ) : null}
                      </div>
                      <span
                        className="h-2 w-2 rounded-full transition-colors bg-white/20 group-hover:bg-brand-yellow"
                        style={active ? { backgroundColor: theme.bg } : undefined}
                      />
                    </div>
                    <p className="body-text mt-4 text-sm line-clamp-3 group-hover:text-brand-white transition-colors">
                      “{t.quote}”
                    </p>
                  </button>
                );
              })}
            </Reveal>
          </div>
        </div>
      </section>

      {/* GET STARTED */}
      {finalCta && (
        <section className="section-pad bg-brand-yellow text-ink-black home-motion-section home-final-cta-section">
          <div className="max-w-[1200px] mx-auto px-5 md:px-10 text-center">
            <Reveal className="home-final-reveal">
              <p className="eyebrow justify-center !text-ink-black/70">[ Get Started ]</p>
              <h2 className="section-headline mt-4 text-ink-black">{finalCta.headline}</h2>
              <p className="body-text mt-6 max-w-2xl mx-auto !text-ink-black/75">
                {finalCta.paragraph}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
                <Link to={finalCta.primary_cta?.link || "/contact"} className="btn-ink" data-testid="home-final-cta">
                  {finalCta.primary_cta?.label || "Contact Us"}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={finalCta.secondary_cta?.link || "/residential-design"}
                  className="btn-outline !text-ink-black !border-ink-black/30 hover:!bg-ink-black hover:!text-brand-white hover:!border-ink-black"
                >
                  {finalCta.secondary_cta?.label || "View Residential Design"}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </main>
  );
}

function HomeTeamCard({ member, index }) {
  const media = getTeamMedia(member);
  const portrait = media.portrait || member.portrait;
  const hoverImage = media.hoverImage || member.hover_image || member.portrait;
  const bio = member.short_bio || member.full_bio;

  return (
    <Reveal delay={index * 120} className="group relative overflow-hidden surface surface-hover bg-ink-charcoal/80 home-card-reveal home-team-card">
      <div className="grid md:grid-cols-[0.86fr_1fr] min-h-[520px]">
        <div className="team-portrait-swap relative min-h-[420px] md:min-h-full bg-ink-graphite overflow-hidden">
          <img
            src={portrait}
            alt={`${member.name} black and white portrait`}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-all duration-[900ms] group-hover:opacity-0 group-hover:scale-105"
          />
          <img
            src={hoverImage}
            alt={`${member.name} color portrait`}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-0 scale-[1.02] transition-all duration-[900ms] group-hover:opacity-100 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-black/85 via-ink-black/15 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-end gap-4">
            <span className="h-8 w-8 border-t-2 border-r-2 border-brand-yellow" />
          </div>
        </div>

        <div className="relative p-7 md:p-9 lg:p-10 flex flex-col justify-end">
          <div className="absolute inset-0 blueprint opacity-[0.14]" />
          <div className="relative z-10">
            <p className="eyebrow">{member.role}</p>
            <h3 className="headline text-3xl md:text-4xl mt-3 text-brand-white">{member.name}</h3>
            {member.credentials ? (
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-brand-yellow">
                {member.credentials}
              </p>
            ) : null}
            <p className="body-text mt-6 text-base md:text-lg">{bio}</p>
            <Link to="/about#team-gallery" className="btn-ghost link-underline mt-7 inline-flex">
              Open Portrait Gallery <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function PathwayCard({ eyebrow, title, image, cta, to, accent = "yellow", delay = 0 }) {
  const accentColor = accent === "green" ? "border-brand-green" : "border-brand-yellow";
  return (
    <Reveal delay={delay} className="relative group aspect-[4/3] overflow-hidden bg-ink-graphite surface home-card-reveal home-pathway-card">
      <Link to={to} className="absolute inset-0">
        <img
          src={image}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-[900ms] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-black via-ink-black/40 to-transparent" />
        <div className="absolute inset-0 p-7 md:p-10 flex flex-col justify-end">
          <p className="eyebrow">{eyebrow}</p>
          <h3 className="headline mt-3 text-3xl md:text-4xl text-brand-white max-w-md">{title}</h3>
          <div className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] font-medium text-brand-white group-hover:text-brand-yellow transition-colors">
            {cta}
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
        <div className={`absolute top-6 right-6 h-6 w-6 border-t-2 border-r-2 ${accentColor}`} />
      </Link>
    </Reveal>
  );
}

function TestimonialCard({ t, visible, index }) {
  const theme = getTestimonialTheme(index);

  return (
    <div
      className={`${
        visible
          ? "relative z-10 opacity-100 translate-y-0 scale-100"
          : "absolute inset-0 z-0 opacity-0 translate-y-6 scale-[0.985] pointer-events-none"
      } transition-all duration-1000 ease-out home-active-testimonial`}
      aria-hidden={!visible}
    >
      <div
        className="relative overflow-hidden p-8 md:p-12 lg:p-14 border"
        style={{
          backgroundColor: theme.bg,
          borderColor: theme.bg,
          color: theme.fg,
        }}
      >
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute inset-0 blueprint opacity-[0.16]" />
        <div className="relative z-10 flex flex-col">
          <div className="flex items-center justify-between gap-6">
            <Quote className="h-10 w-10 md:h-14 md:w-14" style={{ color: theme.quote }} />
            <span className="hidden sm:inline-flex text-[10px] uppercase tracking-[0.26em]" style={{ color: theme.label }}>
              Client Testimonial
            </span>
          </div>

          <p
            className="mt-8 text-xl md:text-2xl lg:text-[1.72rem] leading-relaxed tracking-tight"
            style={{ color: theme.fg }}
          >
            “{t.quote}”
          </p>

          <div className="mt-8 pt-2 flex flex-wrap items-baseline gap-3">
            <p className="font-semibold text-lg tracking-tight">{t.client_name}</p>
            {t.location ? <p className="opacity-70 text-sm">— {t.location}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
